import React, { useCallback, useEffect, useState } from 'react';
import { Button, Text, View } from "../../Components/Themed"
import { ActivityIndicator, FlatList, Image } from 'react-native';
import type { AthleteProps, ExternalPriceProps, LeagueProps, MyPlayerProps, OrderProps, PlayerFilterProps, PlayerPremiumItem, PremiumItemProps, PublicPlayerProps, TeamProps } from '../../types';
import moment from 'moment-mini';
import { AthleteImage, TeamImage } from '../../Components/Jerseys';
import OrderGradeBar from '../../MarketComponents/components/OrderGradeBar';
import { MarketComponentApi, MarketComponentHelpers } from '../../MarketComponents/api';
import { Icons } from '../../Components';
import { useColors } from '../../constants/useColors';
import { PremiumHelpers } from '../../Premium/api';
import ALaCartePremium from '../../Premium/components/ALaCartePremium';

type BetMatchCardProps = {
    player?:PublicPlayerProps,
    me?:MyPlayerProps,
    player_filter?:PlayerFilterProps,
    contest_title:string,
    time_detail:string,
    league?:LeagueProps,
    team?:TeamProps,
    player_premium_items?:PlayerPremiumItem[],
    premium_items?:PremiumItemProps[]
    athlete?:AthleteProps,
    order:OrderProps,
    action_loading:boolean,
    onViewPremium?:(premium_item_id?:string) => void,
    onSelectGrade:(order:OrderProps) => void,
    onViewPlayer:(player:PublicPlayerProps) => void,
    onOrder:(order:OrderProps) => void,
    onReject:(order:OrderProps) => void,
    onPurchasePremium:(pi:PlayerPremiumItem) => void
}
const sections = ['header', 'page', 'offer', 'details','available', 'actions']
const BetMatchCard = ({ me, premium_items, player_filter, player_premium_items, order, action_loading, player, contest_title, league, team, athlete, onViewPremium, onViewPlayer, onPurchasePremium, onOrder, onReject }:BetMatchCardProps) => {
    const Colors = useColors();
    const [ betmatch, setBetMatch ] = useState<{
        loading:boolean,
        external_price?:ExternalPriceProps
    }>({
        loading:false
    })
    const { loading, external_price } = betmatch;

    const premium_external_price = PremiumHelpers.getItemFromIdentifer('external_pricing', premium_items, player_premium_items);
    const premium_order_grades = PremiumHelpers.getItemFromIdentifer('order_grades', premium_items, player_premium_items);
    const show_external_prices = premium_external_price.player_premium_item?.status == 'active' || me?.type == 'premium' ? true : false
    const show_grades = premium_order_grades.player_premium_item?.status == 'active' || me?.type == 'premium' ? true : false

    useEffect(() => {
        MarketComponentApi.setEnvironment();
        getExternalPrice()
    },[order.order_id, show_external_prices]);

    const getExternalPrice = async() => {
        if(!show_external_prices){ return }
        setBetMatch({ loading:true });
        let external_prices = await MarketComponentApi.getExternalPrices(order.event_type, order.event_id, order.market_id, order.side);
        if(order.side_type == 'athlete'){
            external_prices = external_prices.filter(p => p.participant_id == order.side_id);
        }
        if(order.side_type == 'team'){
            external_prices = external_prices.filter(p => p.participant_id == order.side_id);
        }
        //Find consensus!
        let consensus_price = external_prices.find(p => p.external_name.toLowerCase() == 'consensus');
        let wanted_price = external_prices.find(p => p.external_name == player_filter?.filter?.external_book);
        if(wanted_price){ consensus_price = wanted_price }
        if(!consensus_price){ consensus_price = external_prices[0] }
        setTimeout(() => {
            setBetMatch({
                loading:false,
                external_price: consensus_price
            })
        }, 250);
    }

    const renderSections = useCallback((data:{ item:string, index:number }) => {
        switch(data.item){
            case 'header':
                return (
                    <View type='header' style={{ flexDirection:'row', alignItems:'center', padding:10, borderTopRightRadius:8, borderTopLeftRadius:8 }}>
                        <View transparent style={{ flex:1, marginRight:10 }}>
                            <Text theme='h1'>{contest_title}</Text>
                            <Text theme='description' style={{ marginTop:4 }}>Offer expires {moment(order.expire_datetime).fromNow()}</Text>
                        </View>
                        <View transparent style={{ alignItems:'flex-end' }}>
                            <Text theme='h1' color={Colors.text.success}>${order.open_amt.toFixed(2)}</Text>
                            <Text theme='description' style={{ marginTop:3 }}>Available</Text>
                        </View>
                    </View>
                )
            case 'offer':
                return (
                    <View transparent type='row' style={{ margin:10 }}>
                        <View float style={{ borderRadius:100, padding:2 }}>
                            {athlete ?
                            <AthleteImage
                                athlete={athlete}
                                size={36}
                            />
                            :team ?
                            <TeamImage
                                team={team}
                                size={14}
                            />
                            :league ?
                            <Image
                                source={{ uri: league.league_image }}
                                style={{ height:36, width:36, borderRadius:100 }}
                                resizeMode='cover'
                            />
                            :<></>
                            }
                        </View>
                        <View transparent style={{ flex:1, marginLeft:10 }}>
                            <Text theme='h1' size={22}>{order.title}</Text>
                            <Text theme='description' style={{ marginTop:3 }}>Offer expires {moment(order.expire_datetime).fromNow()}</Text>
                        </View>
                    </View>
                )
            case 'details':
                return (
                    <View transparent style={{ margin:10 }}>
                        <View transparent type='row' style={{ padding:10 }}>
                            <View transparent style={{ flex:1 }}>
                                <Text theme='description'>Offer Probability</Text>
                                <Text theme='h1' size={18} style={{ marginTop:4 }}>{(order.probability * 100).toFixed(2)}%</Text>
                            </View>
                            <View transparent style={{ flex:1 }}>
                                <Text theme='description'>Offer Odds</Text>
                                <Text theme='h1' size={18} style={{ marginTop:4 }}>{MarketComponentHelpers.getOddsLabel(order.odds)}</Text>
                            </View>
                        </View>
                        {show_external_prices ?
                        <View transparent type='row' style={{ padding:10 }}>
                            <View transparent style={{ flex:1 }}>
                                <Text theme='description'>External Price</Text>
                                {loading ?
                                <ActivityIndicator size={20} color={Colors.text.h1} style={{ alignSelf:'flex-start', marginTop:4 }} />
                                : !show_external_prices ?
                                <Text theme='error' size={14} style={{ marginTop:4 }}>{!show_external_prices ? 'External prices not enabled for account' : external_price ? external_price.external_name: 'Unable to find'}</Text>
                                :
                                <Text theme='h1' size={18} style={{ marginTop:4 }}>{external_price ? external_price.external_name: 'Unable to find'}</Text>
                                }
                            </View>
                            <View transparent style={{ flex:1 }}>
                                <Text theme='description'>Suggested Odds</Text>
                                <Text theme='h1' size={18} style={{ marginTop:4 }}>{external_price ? MarketComponentHelpers.getOddsLabel(external_price.odds): '...'}</Text>
                            </View>
                        </View>
                        :premium_external_price.premium_item ?
                            <ALaCartePremium
                                style={{ margin:5 }}
                                premium_item={premium_external_price.premium_item}
                                player_premium_item={premium_external_price.player_premium_item}
                                player={me}
                                identifier='external_pricing'
                                onPurchaseItem={onPurchasePremium}
                                onViewPremium={(id) => onViewPremium ? onViewPremium(id) : console.log('')}
                            />
                        :<></>}
                        {show_grades ?
                        <View transparent type='row' style={{ padding:10 }}>
                            <View transparent style={{ flex:1 }}>
                                <Text theme='description'>Offer Grade</Text>
                                {!order?.grade || !show_grades ?
                                <Text style={{ marginTop:3 }} theme="h1">N/A</Text>
                                :order.grade > 105 ?
                                <Text style={{ marginTop:3 }} theme="h1" color={Colors.text.success}>Amazing Price!</Text>
                                : order.grade > 98 ?
                                <Text style={{ marginTop:3 }} theme="h1" color={Colors.text.success}>Very Competitive!</Text>
                                : order.grade > 95 ?
                                <Text style={{ marginTop:3 }} theme="h1" color={Colors.text.warning}>Somewhat Competitive.</Text>
                                :
                                <Text style={{ marginTop:3 }} theme="h1" color={Colors.text.error}>Not Competitive.</Text>
                                }
                                <Text theme='h1' size={18} style={{ marginTop:4 }}></Text>
                            </View>
                            <OrderGradeBar mask={!show_grades} grade={order.grade} view_type='card' onSelectGrade={() => console.log('')}/>
                        </View>
                        :premium_order_grades.premium_item ?
                            <ALaCartePremium
                                style={{ margin:5 }}
                                premium_item={premium_order_grades.premium_item}
                                player_premium_item={premium_order_grades.player_premium_item}
                                player={me}
                                identifier='order_grades'
                                onPurchaseItem={onPurchasePremium}
                                onViewPremium={(id) => onViewPremium ? onViewPremium(id) : console.log('')}
                            />
                        :<></>}
                    </View>
                )
            case 'available':
                return (
                    <View transparent style={{ margin:15 }}>
                        <View transparent type='row' style={{ padding:15 }}>
                            <View transparent style={{ flex:1 }}>
                                <Text theme='description'>Available</Text>
                                <Text theme='h1' size={24} style={{ marginTop:4 }}>${order.open_amt.toFixed(2)}</Text>
                            </View>
                            <View transparent style={{ flex:1 }}>
                                <Text theme='description'>Potential Winnings</Text>
                                <Text theme='h1' size={24} style={{ marginTop:4 }}>${order.potential_winnings.toFixed(2)}</Text>
                            </View>
                        </View>
                    </View>
                )
            default: return <></>
        }
    }, [JSON.stringify(external_price), order.order_id])

    return (
        <View float style={{ flex:1 }}>
            <View float transparent style={{ marginBottom:30, flex:1 }}>
                <View float type='footer' style={{ position:'absolute', left:0, right:0, bottom: -30, height:50, margin:0, borderRadius:8 }} />
                <View float type='footer' style={{ position:'absolute', left:0, right:0, bottom: -15, height:50, margin:-5, borderRadius:8 }} />
                <View transparent style={{ flex:1 }}>
                    <FlatList
                        data={sections}
                        key={'bet_match_card_sections'}
                        keyExtractor={(item) => item}
                        renderItem={renderSections}
                    />
                </View>
                <View type='footer' style={{ flexDirection:'row', padding:5 }}>
                    <Button float disabled={action_loading} loading={action_loading} style={{ flex:1, marginRight:4, flexDirection:'row', justifyContent:'center', alignItems:'center', opacity: action_loading?0.5:1 }} onPress={() => onReject(order)}>
                        <Icons.CloseIcon size={14} color={Colors.text.error}/>
                        <Text theme='h2' style={{ marginLeft:10 }} color={Colors.text.error}>Decline</Text>
                    </Button>
                    <Button float disabled={action_loading} loading={action_loading} style={{ flex:1, marginLeft:4, flexDirection:'row', justifyContent:'center', alignItems:'center', opacity: action_loading?0.5:1 }} onPress={() => onOrder(order)}>
                        <Icons.CheckIcon size={14} color={Colors.text.success}/>
                        <Text theme='h2' style={{ marginLeft:10 }} color={Colors.text.success}>Accept</Text>
                    </Button>
                </View>
                {player ?
                <View float type='footer' style={{ flexDirection:'row', alignItems:'center', borderRadius:8, margin:-10, marginTop:0, padding:10 }}>
                    <Button transparent float style={{ padding:2, borderRadius:100 }} onPress={() => onViewPlayer(player)}>
                        <Image
                            source={{ uri: player.profile_pic && player.profile_pic != '' ? player.profile_pic : 'https://res.cloudinary.com/hoabts6mc/image/upload/v1722453927/default_man_n96ofq.webp' }}
                            style={{ height:40, width:40, borderRadius:100}}
                            resizeMode='cover'
                        />
                    </Button>
                    <View transparent style={{ flex:1, marginLeft:15 }}>
                        <Text theme='h1'>@{player.username}</Text>
                        <Text theme='description' style={{ marginTop:3 }}>Joined {moment(player.create_datetime).fromNow()}</Text>
                    </View>
                    <View transparent style={{ alignItems:'center' }}>
                        <Icons.LocationIcon size={18} color={Colors.text.h1}/>
                        <Text theme='h2'>{order.region}</Text>
                    </View>
                </View>
                :<></>}
            </View>   
        </View>
    )
}

/*
<Image
    source={{ uri: 'https://res.cloudinary.com/hoabts6mc/image/upload/v1647289869/ellipse_background_fhh1ut.webp' }}
    style={{ height:400, width:400 }}
    resizeMode='cover'
/>
*/

export default BetMatchCard